import React, { useState, useCallback } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../components/ui/use-toast';
import { buildMonthOptions, currentMonthValue, monthToPeriod } from '../../utils/monthPeriod';
import {
  BuildingOfficeIcon,
  PlusIcon,
  MapPinIcon,
  PhoneIcon,
  ChartBarIcon,
  CalendarIcon,
  FunnelIcon,
  PencilIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  BellIcon,
  ArchiveBoxArrowDownIcon,
} from '@heroicons/react/24/outline';
import { downloadCompletionDocumentsZip } from '../../utils/completionDocuments';

const RECONCILIATION_LABELS = {
  approved: { label: 'Mutabıkız', color: 'bg-green-100 text-green-700 border-green-200' },
  rejected: { label: 'Mutabık Değiliz', color: 'bg-red-100 text-red-700 border-red-200' },
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
};

const statusLabels = {
  initiated: 'Başlatıldı',
  in_progress: 'Devam Ediyor',
  completed: 'Tamamlandı',
  cancelled: 'İptal',
};

const HospitalsPage = () => {
  const { user } = useAuthStore();
  const isReadOnly = user?.role === 'external_presidency' || user?.role === 'external_hospital';
  const isExternalHospital = user?.role === 'external_hospital';
  const canReconcile = ['super_admin', 'admin', 'external_hospital', 'external_presidency'].includes(user?.role);

  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  const [editingHospital, setEditingHospital] = useState(null);
  const [formData, setFormData] = useState({
    name: '', code: '', type: '', city: '', district: '',
    address: '', phone: '', department_id: '', monthly_quota: '', quota_note: '', price_per_case: '',
    contact_email_1: '', contact_name_1: '',
    contact_email_2: '', contact_name_2: '',
    contact_email_3: '', contact_name_3: '',
  });

  // Hospital detail modal states
  const [detailHospital, setDetailHospital] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailStartDate, setDetailStartDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [detailEndDate, setDetailEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [showQuotaEdit, setShowQuotaEdit] = useState(false);
  const [quotaFormData, setQuotaFormData] = useState({ monthly_quota: '', quota_note: '', price_per_case: '' });

  // Mutabakat UI states
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [casePopup, setCasePopup] = useState(null); // { case, x, y }
  const [statusFilter, setStatusFilter] = useState([]); // [] = tümü göster

  // Email UI states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailType, setEmailType] = useState('reminder'); // 'reminder' | 'reconciliation_complete'
  const [emailMonth, setEmailMonth] = useState(() => currentMonthValue()); // "YYYY-MM"
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const monthOptions = React.useMemo(() => buildMonthOptions(18), []);
  const emailPeriod = monthToPeriod(emailMonth);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: institutionsResponse, isLoading: institutionsLoading } = useQuery({
    queryKey: ['institutions-all'],
    queryFn: () => api.get('/institutions').then(res => {
      const payload = res.data;
      if (payload?.data && Array.isArray(payload.data)) return payload.data;
      if (Array.isArray(payload)) return payload;
      return [];
    }),
    staleTime: 60000,
  });
  const departments = (institutionsResponse || [])
    .filter(inst => (inst.name || '').toUpperCase().includes('BAŞKANLIK'))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));

  const { data: hospitalsResponse, isLoading, error } = useQuery({
    queryKey: ['hospitals'],
    queryFn: () => api.get('/hospitals').then(res => res.data),
    retry: 3,
  });
  const hospitalsData = hospitalsResponse?.data || [];

  const { data: hospitalCasesResponse, isLoading: casesLoading } = useQuery({
    queryKey: ['hospital-cases', detailHospital?.id, detailStartDate, detailEndDate],
    queryFn: () => {
      const params = new URLSearchParams();
      if (detailStartDate) params.append('startDate', detailStartDate);
      if (detailEndDate) params.append('endDate', detailEndDate);
      return api.get(`/hospitals/${detailHospital.id}/cases?${params.toString()}`).then(res => res.data);
    },
    enabled: !!detailHospital && showDetailModal,
  });
  const hospitalCases = hospitalCasesResponse?.data || [];

  const hospitalCountQueries = useQueries({
    queries: hospitalsData.map(h => ({
      queryKey: ['hospital-case-count', h.id, startDate, endDate],
      queryFn: async () => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        const res = await api.get(`/hospitals/${h.id}/cases?${params.toString()}`);
        return { id: h.id, count: (res.data?.data || []).length };
      },
      staleTime: 60000,
      enabled: !!startDate && !!endDate,
    })),
  });

  const quotaStatsMap = {};
  hospitalCountQueries.forEach(query => {
    if (query.data) quotaStatsMap[query.data.id] = query.data.count;
  });
  if (detailHospital && hospitalCases.length > 0) {
    quotaStatsMap[detailHospital.id] = hospitalCases.length;
  }

  // ─── Mutations ────────────────────────────────────────────────────────────

  const createHospitalMutation = useMutation({
    mutationFn: (data) => api.post('/hospitals', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['hospitals']);
      toast({ title: 'Başarılı', description: 'Hastane başarıyla eklendi' });
      handleCloseModal();
    },
    onError: (error) => {
      toast({ title: 'Hata', description: error.response?.data?.error || 'Hastane eklenirken hata oluştu', variant: 'destructive' });
    }
  });

  const updateHospitalMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/hospitals/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['hospitals']);
      toast({ title: 'Başarılı', description: 'Hastane başarıyla güncellendi' });
      handleCloseModal();
    },
    onError: (error) => {
      toast({ title: 'Hata', description: error.response?.data?.error || 'Hastane güncellenirken hata oluştu', variant: 'destructive' });
    }
  });

  const deleteHospitalMutation = useMutation({
    mutationFn: (id) => api.delete(`/hospitals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['hospitals']);
      toast({ title: 'Başarılı', description: 'Hastane başarıyla silindi' });
    },
    onError: (error) => {
      toast({ title: 'Hata', description: error.response?.data?.error || 'Hastane silinirken hata oluştu', variant: 'destructive' });
    }
  });

  const updateQuotaMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/hospitals/${id}/quota`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['hospitals']);
      toast({ title: 'Başarılı', description: 'Kota başarıyla güncellendi' });
      setShowQuotaEdit(false);
      if (detailHospital) {
        const updated = hospitalsData.find(h => h.id === detailHospital.id);
        if (updated) setDetailHospital({ ...updated, ...quotaFormData });
      }
    },
    onError: () => {
      toast({ title: 'Hata', description: 'Kota güncellenirken hata oluştu', variant: 'destructive' });
    }
  });

  const reconciliationMutation = useMutation({
    mutationFn: (data) => api.patch('/cases/reconciliation', data),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries(['hospital-cases']);
      setSelectedCaseIds([]);
      const label = variables.status === 'approved' ? 'Mutabıkız' : 'Mutabık Değiliz';
      toast({ title: 'Başarılı', description: `${res.data?.updated_count || 0} vaka "${label}" olarak işaretlendi` });
    },
    onError: (error) => {
      toast({ title: 'Hata', description: error.response?.data?.error || 'Mutabakat güncellenirken hata oluştu', variant: 'destructive' });
    }
  });

  const sendReminderNotifMutation = useMutation({
    mutationFn: (hospitalId) => api.post(`/hospitals/${hospitalId}/send-reminder-notification`),
    onSuccess: (res) => {
      toast({ title: 'Başarılı', description: res.data?.message || 'Bildirim gönderildi' });
    },
    onError: (error) => {
      toast({ title: 'Hata', description: error.response?.data?.error || 'Bildirim gönderilemedi', variant: 'destructive' });
    }
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const filteredHospitals = hospitalsData.filter(hospital => {
    const matchesDepartment = !selectedDepartment || hospital.institution_id?.toString() === selectedDepartment;
    const matchesSearch = !searchTerm ||
      (hospital.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (hospital.city || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (hospital.district || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (hospital.institution_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesDepartment && matchesSearch;
  });

  const handleOpenModal = (hospital = null) => {
    setEditingHospital(hospital);
    setFormData(hospital ? {
      name: hospital.name || '',
      code: hospital.code || '',
      type: hospital.type || '',
      city: hospital.city || '',
      district: hospital.district || '',
      address: hospital.address || '',
      phone: hospital.phone || '',
      department_id: hospital.institution_id?.toString() || '',
      monthly_quota: hospital.monthly_quota?.toString() || '0',
      quota_note: hospital.quota_note || '',
      price_per_case: hospital.price_per_case?.toString() || '0',
      contact_email_1: hospital.contact_email_1 || '',
      contact_name_1: hospital.contact_name_1 || '',
      contact_email_2: hospital.contact_email_2 || '',
      contact_name_2: hospital.contact_name_2 || '',
      contact_email_3: hospital.contact_email_3 || '',
      contact_name_3: hospital.contact_name_3 || '',
    } : {
      name: '', code: '', type: '', city: '', district: '',
      address: '', phone: '', department_id: '',
      monthly_quota: '0', quota_note: '', price_per_case: '0',
      contact_email_1: '', contact_name_1: '',
      contact_email_2: '', contact_name_2: '',
      contact_email_3: '', contact_name_3: '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHospital(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.type) {
      toast({ title: 'Hata', description: 'Hastane adı ve türü zorunludur', variant: 'destructive' });
      return;
    }
    const submitData = {
      ...formData,
      institution_id: formData.department_id ? parseInt(formData.department_id) : null,
      monthly_quota: parseInt(formData.monthly_quota) || 0,
      price_per_case: parseFloat(formData.price_per_case) || 0,
    };
    if (editingHospital) {
      updateHospitalMutation.mutate({ id: editingHospital.id, data: submitData });
    } else {
      createHospitalMutation.mutate(submitData);
    }
  };

  const handleDelete = (hospitalId) => {
    if (window.confirm('Bu hastaneyi silmek istediğinizden emin misiniz?')) {
      deleteHospitalMutation.mutate(hospitalId);
    }
  };

  const handleOpenDetail = (hospital) => {
    setDetailHospital(hospital);
    setShowDetailModal(true);
    setShowQuotaEdit(false);
    setSelectedCaseIds([]);
    setStatusFilter([]);
    setQuotaFormData({
      monthly_quota: hospital.monthly_quota?.toString() || '0',
      quota_note: hospital.quota_note || '',
      price_per_case: hospital.price_per_case?.toString() || '0',
    });
  };

  const handleSaveQuota = () => {
    if (!detailHospital) return;
    updateQuotaMutation.mutate({
      id: detailHospital.id,
      data: {
        monthly_quota: parseInt(quotaFormData.monthly_quota) || 0,
        quota_note: quotaFormData.quota_note || null,
        price_per_case: parseFloat(quotaFormData.price_per_case) || 0,
      }
    });
  };

  const handleReconciliation = async () => {
    if (!detailHospital) return;
    try {
      toast({ title: 'Hazırlanıyor...', description: 'Mutabakat formu oluşturuluyor' });
      const params = new URLSearchParams();
      if (detailStartDate) params.append('startDate', detailStartDate);
      if (detailEndDate) params.append('endDate', detailEndDate);
      const response = await api.get(`/hospitals/${detailHospital.id}/reconciliation?${params.toString()}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mutabakat_${detailHospital.name.replace(/[^a-z0-9]/gi, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Başarılı', description: 'Mutabakat formu indirildi' });
    } catch (err) {
      console.error('Reconciliation error:', err);
      toast({ title: 'Hata', description: 'Mutabakat formu oluşturulurken hata oluştu', variant: 'destructive' });
    }
  };

  const [isDownloadingDocs, setIsDownloadingDocs] = useState(false);
  const handleDownloadCompletionDocs = async () => {
    if (!detailHospital) return;
    setIsDownloadingDocs(true);
    toast({ title: 'Hazırlanıyor...', description: 'Tamamlama belgeleri toplanıyor, bu işlem birkaç dakika sürebilir' });
    try {
      await downloadCompletionDocumentsZip('hospitals', detailHospital.id, detailHospital.name, detailStartDate, detailEndDate);
      toast({ title: 'Başarılı', description: 'Tamamlama belgeleri indirildi' });
    } catch (err) {
      toast({ title: 'Hata', description: err.message, variant: 'destructive' });
    } finally {
      setIsDownloadingDocs(false);
    }
  };

  // Checkbox handler: toggle one case
  const toggleCaseSelect = (caseId) => {
    setSelectedCaseIds(prev =>
      prev.includes(caseId) ? prev.filter(id => id !== caseId) : [...prev, caseId]
    );
  };

  // Select all / deselect all (filtrelenmiş vakalar üzerinde)
  const toggleSelectAll = () => {
    const visibleIds = filteredHospitalCases.map(c => c.id);
    const allSelected = visibleIds.every(id => selectedCaseIds.includes(id));
    if (allSelected) {
      setSelectedCaseIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedCaseIds(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const handleApprove = () => {
    if (selectedCaseIds.length === 0) {
      toast({ title: 'Uyarı', description: 'Lütfen en az bir vaka seçin', variant: 'destructive' });
      return;
    }
    reconciliationMutation.mutate({ case_ids: selectedCaseIds, status: 'approved' });
  };

  const handleRejectOpen = () => {
    if (selectedCaseIds.length === 0) {
      toast({ title: 'Uyarı', description: 'Lütfen en az bir vaka seçin', variant: 'destructive' });
      return;
    }
    setRejectNote('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectNote.trim()) {
      toast({ title: 'Hata', description: '"Mutabık Değiliz" için gerekçe açıklaması zorunludur', variant: 'destructive' });
      return;
    }
    reconciliationMutation.mutate({ case_ids: selectedCaseIds, status: 'rejected', note: rejectNote.trim() });
    setShowRejectModal(false);
    setRejectNote('');
  };

  const handleSendEmail = async () => {
    if (!detailHospital) return;
    setIsSendingEmail(true);
    try {
      const emailRecipients = [
        detailHospital.contact_email_1,
        detailHospital.contact_email_2,
        detailHospital.contact_email_3,
      ].filter(Boolean);

      if (emailRecipients.length === 0) {
        toast({ title: 'Uyarı', description: 'Bu hastane için kayıtlı e-posta adresi bulunamadı. Önce hastane düzenleme formundan e-posta ekleyin.', variant: 'destructive' });
        setIsSendingEmail(false);
        return;
      }

      const res = await api.post(`/hospitals/${detailHospital.id}/send-email`, {
        type: emailType,
        period_start: emailPeriod?.startDate || detailStartDate,
        period_end: emailPeriod?.endDate || detailEndDate,
      });
      toast({ title: 'Başarılı', description: res.data?.message || 'E-posta gönderildi' });
      setShowEmailModal(false);
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.error || 'E-posta gönderilemedi', variant: 'destructive' });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getHospitalTypeBadge = (type) => {
    const typeConfig = {
      'DEVLET_HASTANESI': { label: 'Devlet Hastanesi', color: 'bg-blue-100 text-blue-800' },
      'EGITIM_ARASTIRMA': { label: 'Eğitim Araştırma', color: 'bg-green-100 text-green-800' },
      'OZEL_HASTANE': { label: 'Özel Hastane', color: 'bg-purple-100 text-purple-800' },
      'UNIVERSITESI': { label: 'Üniversite Hastanesi', color: 'bg-orange-100 text-orange-800' },
      'SAGLIK_MERKEZI': { label: 'Sağlık Merkezi', color: 'bg-teal-100 text-teal-800' },
    };
    const config = typeConfig[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getQuotaPercent = (hospitalId, quota) => {
    if (!quota || quota === 0) return null;
    const used = quotaStatsMap[hospitalId] || 0;
    return { used, quota, percent: Math.min(100, Math.round((used / quota) * 100)) };
  };

  const getReconciliationBadge = (c) => {
    const st = c.reconciliation_status;
    if (!st || st === 'pending') return RECONCILIATION_LABELS.pending;
    return RECONCILIATION_LABELS[st] || RECONCILIATION_LABELS.pending;
  };

  // Summary of reconciliation for the current case list (tüm vakalar üzerinden)
  const reconcSummary = {
    total: hospitalCases.length,
    approved: hospitalCases.filter(c => c.reconciliation_status === 'approved').length,
    rejected: hospitalCases.filter(c => c.reconciliation_status === 'rejected').length,
    pending: hospitalCases.filter(c => !c.reconciliation_status || c.reconciliation_status === 'pending').length,
  };

  // Durum filtresine göre görüntülenecek vakalar
  const filteredHospitalCases = statusFilter.length === 0
    ? hospitalCases
    : hospitalCases.filter(c => statusFilter.includes(c.status));

  const toggleStatusFilter = (status) => {
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
    setSelectedCaseIds([]);
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Hastaneler</h1>
          <p className="text-gray-600">Hastane bilgilerini görüntüleyin, filtreleyip istatistiklerini inceleyin</p>
        </div>
        {!isReadOnly && (
          <Button className="inline-flex items-center" onClick={() => handleOpenModal()}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Yeni Hastane
          </Button>
        )}
      </div>

      {/* Filters - external_hospital için basitleştirilmiş */}
      {!isExternalHospital && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FunnelIcon className="h-5 w-5 mr-2" />
              Filtreler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Hastane Ara</Label>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                  </svg>
                  <Input type="text" placeholder="Hastane adı, şehir veya ilçe..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Başkanlık</Label>
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger>
                    <SelectValue placeholder={institutionsLoading ? 'Yükleniyor...' : 'Tüm başkanlıklar'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Tüm başkanlıklar</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id.toString()}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Başlangıç Tarihi <span className="text-xs text-gray-400">(Kota için)</span></Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Bitiş Tarihi <span className="text-xs text-gray-400">(Kota için)</span></Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hospital List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Hastane Listesi</span>
            <Badge variant="secondary">{filteredHospitals.length} / {hospitalsData.length} hastane</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <Card key={i}><CardContent className="p-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </CardContent></Card>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-2">Hata: {error.message}</p>
              <Button variant="outline" onClick={() => window.location.reload()}>Yeniden Dene</Button>
            </div>
          ) : filteredHospitals.length === 0 ? (
            <div className="text-center py-8">
              <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">{searchTerm || selectedDepartment ? 'Arama kriterlerine uygun hastane bulunamadı.' : 'Henüz hastane kaydı bulunmuyor.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredHospitals.map((hospital) => {
                const quotaInfo = getQuotaPercent(hospital.id, hospital.monthly_quota);
                return (
                  <Card key={hospital.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleOpenDetail(hospital)}>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-start justify-between">
                        <div className="flex items-start space-x-2">
                          <BuildingOfficeIcon className="h-5 w-5 mt-0.5 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm leading-tight">{hospital.name}</h3>
                            <p className="text-xs text-gray-500 mt-1">ID: {hospital.id}</p>
                          </div>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-3">
                      <div className="flex items-center space-x-2">
                        {getHospitalTypeBadge(hospital.type)}
                      </div>
                      <div className="flex items-start space-x-2 text-sm">
                        <MapPinIcon className="h-4 w-4 mt-0.5 text-orange-500" />
                        <div className="flex-1">
                          <div className="font-medium text-orange-700">{hospital.institution_name || hospital.department_name || 'Başkanlık atanmamış'}</div>
                          <div className="text-gray-500">{hospital.district}</div>
                          {hospital.address && <div className="text-xs text-gray-500 mt-1">{hospital.address}</div>}
                        </div>
                      </div>
                      {hospital.monthly_quota > 0 && quotaInfo && (
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs font-medium">
                            <span>Genel Kota</span>
                            <span className={quotaInfo.percent >= 90 ? 'text-red-600' : quotaInfo.percent >= 70 ? 'text-yellow-600' : 'text-green-600'}>
                              {quotaInfo.used} / {quotaInfo.quota}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className={`h-2 rounded-full transition-all ${quotaInfo.percent >= 90 ? 'bg-red-500' : quotaInfo.percent >= 70 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${quotaInfo.percent}%` }} />
                          </div>
                          <p className="text-xs text-gray-500">%{quotaInfo.percent} doluluk</p>
                        </div>
                      )}
                      {hospital.monthly_quota === 0 && <p className="text-xs text-gray-400">Kota tanımlanmamış</p>}
                      <div className="pt-3 border-t flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant={hospital.is_active ? 'default' : 'destructive'} className="text-xs">{hospital.is_active ? 'Aktif' : 'Pasif'}</Badge>
                          {hospital.price_per_case > 0 && <span className="text-xs text-gray-600 font-medium">{hospital.price_per_case.toLocaleString('tr-TR')} TL/vaka</span>}
                        </div>
                        {!isReadOnly && (
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50" onClick={(e) => { e.stopPropagation(); handleOpenModal(hospital); }} title="Düzenle">
                              <PencilIcon className="h-3.5 w-3.5 mr-1" />
                              <span className="text-xs">Düzenle</span>
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); handleDelete(hospital.id); }} title="Sil">
                              <XMarkIcon className="h-3.5 w-3.5 mr-1" />
                              <span className="text-xs">Sil</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Hospital Detail Modal ─────────────────────────────────────── */}
      {showDetailModal && detailHospital && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold">{detailHospital.name}</h2>
                <p className="text-sm text-gray-500">
                  {detailHospital.institution_name || detailHospital.department_name || 'Başkanlık atanmamış'}
                  {detailHospital.district && ` • ${detailHospital.district}`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)}>
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Kota Bilgileri */}
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                    <ChartBarIcon className="h-5 w-5" />
                    Kota Bilgileri
                  </h3>
                  <div className="flex gap-2">
                    {/* E-posta gönder butonu (sadece admin/super_admin) */}
                    {['super_admin', 'admin'].includes(user?.role) && (
                      <Button size="sm" variant="outline" onClick={() => setShowEmailModal(true)}>
                        <EnvelopeIcon className="h-4 w-4 mr-1" />
                        Mail Gönder
                      </Button>
                    )}
                    {/* Bildirim gönder butonu (sadece admin/super_admin) */}
                    {['super_admin', 'admin'].includes(user?.role) && (
                      <Button size="sm" variant="outline" onClick={() => sendReminderNotifMutation.mutate(detailHospital.id)} disabled={sendReminderNotifMutation.isLoading}>
                        <BellIcon className="h-4 w-4 mr-1" />
                        {sendReminderNotifMutation.isLoading ? 'Gönderiliyor...' : 'Bildirim Gönder'}
                      </Button>
                    )}
                    {!isReadOnly && (
                      <Button size="sm" variant="outline" onClick={() => setShowQuotaEdit(!showQuotaEdit)}>
                        <PencilIcon className="h-4 w-4 mr-1" />
                        {showQuotaEdit ? 'İptal' : 'Kota Düzenle'}
                      </Button>
                    )}
                  </div>
                </div>

                {showQuotaEdit ? (
                  <div className="space-y-3 bg-white rounded p-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Genel Kota</Label>
                        <Input type="number" value={quotaFormData.monthly_quota} onChange={(e) => setQuotaFormData(prev => ({ ...prev, monthly_quota: e.target.value }))} placeholder="0" min="0" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Vaka Başı Ücret (TL)</Label>
                        <Input type="number" value={quotaFormData.price_per_case} onChange={(e) => setQuotaFormData(prev => ({ ...prev, price_per_case: e.target.value }))} placeholder="0" min="0" step="0.01" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Kota Notu</Label>
                        <Input value={quotaFormData.quota_note} onChange={(e) => setQuotaFormData(prev => ({ ...prev, quota_note: e.target.value }))} placeholder="Açıklama..." />
                      </div>
                    </div>
                    <Button size="sm" onClick={handleSaveQuota} disabled={updateQuotaMutation.isLoading}>
                      {updateQuotaMutation.isLoading ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-700">{detailHospital.monthly_quota || 0}</p>
                      <p className="text-xs text-gray-500">Genel Kota</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-700">{hospitalCases.length}</p>
                      <p className="text-xs text-gray-500">Toplam Vaka</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-700">{detailHospital.price_per_case || 0} TL</p>
                      <p className="text-xs text-gray-500">Vaka Başı Ücret</p>
                    </div>
                  </div>
                )}

                {detailHospital.quota_note && !showQuotaEdit && (
                  <p className="text-xs text-blue-600 italic">{detailHospital.quota_note}</p>
                )}

                {/* E-posta listesi özeti */}
                {(detailHospital.contact_email_1 || detailHospital.contact_email_2 || detailHospital.contact_email_3) && !showQuotaEdit && (
                  <div className="mt-2 text-xs text-blue-700 space-y-0.5">
                    {[1, 2, 3].map(i => detailHospital[`contact_email_${i}`] && (
                      <div key={i}>{detailHospital[`contact_name_${i}`] ? `${detailHospital[`contact_name_${i}`]} — ` : ''}{detailHospital[`contact_email_${i}`]}</div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mutabakat Oluştur (Excel) */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  Mutabakat Oluştur
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Başlangıç Tarihi</Label>
                    <Input type="date" value={detailStartDate} onChange={(e) => { setDetailStartDate(e.target.value); setSelectedCaseIds([]); }} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Bitiş Tarihi</Label>
                    <Input type="date" value={detailEndDate} onChange={(e) => { setDetailEndDate(e.target.value); setSelectedCaseIds([]); }} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button onClick={handleReconciliation} className="w-full">
                    📊 Mutabakat Formu Oluştur (Excel)
                  </Button>
                  <Button
                    onClick={handleDownloadCompletionDocs}
                    disabled={isDownloadingDocs}
                    variant="outline"
                    className="w-full border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                  >
                    <ArchiveBoxArrowDownIcon className="h-4 w-4 mr-2" />
                    {isDownloadingDocs ? 'Hazırlanıyor...' : 'Tamamlama Belgelerini İndir (ZIP)'}
                  </Button>
                </div>
              </div>

              {/* Vakalar + Mutabakat Tablosu */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <PhoneIcon className="h-5 w-5" />
                    Vakalar ({detailStartDate} - {detailEndDate})
                    <Badge variant="secondary">{hospitalCases.length} vaka</Badge>
                  </h3>

                  {/* Mutabakat özet */}
                  {hospitalCases.length > 0 && (
                    <div className="flex gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700">{reconcSummary.approved} onaylı</span>
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700">{reconcSummary.rejected} reddedilen</span>
                      <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">{reconcSummary.pending} bekleyen</span>
                    </div>
                  )}
                </div>

                {/* Toplu aksiyon butonları + durum filtre checkbox'ları */}
                {canReconcile && hospitalCases.length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="text-sm text-gray-600">{selectedCaseIds.length} vaka seçildi</span>
                    <Button size="sm" variant="outline" className="border-green-500 text-green-700 hover:bg-green-50" onClick={handleApprove} disabled={selectedCaseIds.length === 0 || reconciliationMutation.isLoading}>
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Mutabıkız
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500 text-red-700 hover:bg-red-50" onClick={handleRejectOpen} disabled={selectedCaseIds.length === 0 || reconciliationMutation.isLoading}>
                      <XCircleIcon className="h-4 w-4 mr-1" />
                      Mutabık Değiliz
                    </Button>

                    {/* Durum filtre checkbox'ları */}
                    <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-200 flex-wrap">
                      <span className="text-xs text-gray-500 font-medium">Durum:</span>
                      {[
                        { value: 'initiated',   label: 'Başlatıldı',    bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
                        { value: 'in_progress', label: 'Devam Ediyor',  bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300' },
                        { value: 'completed',   label: 'Tamamlandı',    bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300' },
                        { value: 'cancelled',   label: 'İptal Edildi',  bg: 'bg-red-100',    text: 'text-red-800',    border: 'border-red-300' },
                      ].map(({ value, label, bg, text, border }) => {
                        const checked = statusFilter.includes(value);
                        const count = hospitalCases.filter(c => c.status === value).length;
                        return (
                          <label
                            key={value}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-full border cursor-pointer select-none text-xs font-medium transition-colors ${checked ? `${bg} ${text} ${border}` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleStatusFilter(value)}
                              className="w-3 h-3 cursor-pointer rounded"
                            />
                            {label}
                            <span className={`text-xs ${checked ? 'opacity-80' : 'text-gray-400'}`}>({count})</span>
                          </label>
                        );
                      })}
                      {statusFilter.length > 0 && (
                        <button
                          onClick={() => { setStatusFilter([]); setSelectedCaseIds([]); }}
                          className="text-xs text-gray-400 hover:text-gray-600 underline ml-1"
                        >
                          Temizle
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {casesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : hospitalCases.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4 text-center">Bu dönemde vaka bulunamadı.</p>
                ) : filteredHospitalCases.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4 text-center">Seçili duruma ait vaka bulunamadı.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          {canReconcile && (
                            <th className="p-2 w-8">
                              <input
                                type="checkbox"
                                checked={filteredHospitalCases.length > 0 && filteredHospitalCases.every(c => selectedCaseIds.includes(c.id))}
                                onChange={toggleSelectAll}
                                className="rounded cursor-pointer"
                              />
                            </th>
                          )}
                          <th className="text-left p-2">Vaka No</th>
                          <th className="text-left p-2">Tarih</th>
                          <th className="text-left p-2">Hasta</th>
                          <th className="text-left p-2">Tür</th>
                          <th className="text-left p-2">Durum</th>
                          {detailHospital?.price_per_case > 0 && <th className="text-right p-2">Fiyat</th>}
                          <th className="text-left p-2">Mutabakat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHospitalCases.map((c) => {
                          const rBadge = getReconciliationBadge(c);
                          return (
                            <tr key={c.id} className={`border-b hover:bg-gray-50 ${selectedCaseIds.includes(c.id) ? 'bg-blue-50' : ''}`}>
                              {canReconcile && (
                                <td className="p-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedCaseIds.includes(c.id)}
                                    onChange={() => toggleCaseSelect(c.id)}
                                    className="rounded cursor-pointer"
                                  />
                                </td>
                              )}
                              <td className="p-2">
                                {/* Vaka no popup */}
                                <button
                                  className="font-mono text-blue-600 underline cursor-pointer hover:text-blue-800 text-xs"
                                  onClick={() => setCasePopup(casePopup?.case?.id === c.id ? null : { case: c })}
                                >
                                  {c.case_number}
                                </button>
                                {/* Popup */}
                                {casePopup?.case?.id === c.id && (
                                  <div className="absolute z-50 mt-1 bg-white border rounded-lg shadow-lg p-3 w-72 text-xs">
                                    <div className="flex items-center justify-between mb-2">
                                      <strong className="text-sm">Vaka Özeti</strong>
                                      <button onClick={() => setCasePopup(null)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-4 w-4" /></button>
                                    </div>
                                    <div className="space-y-1.5">
                                      <div><span className="font-medium text-gray-600">Hasta:</span> {c.patient_name} {c.patient_surname}</div>
                                      <div><span className="font-medium text-gray-600">Tarih:</span> {c.request_date ? new Date(c.request_date).toLocaleDateString('tr-TR') : '-'}</div>
                                      <div><span className="font-medium text-gray-600">Tür:</span> {c.transport_type || '-'}</div>
                                      {c.patient_address && <div><span className="font-medium text-gray-600">Kaynak:</span> {c.patient_address}</div>}
                                      {c.destination_address && <div><span className="font-medium text-gray-600">Hedef:</span> {c.destination_address}</div>}
                                      <div><span className="font-medium text-gray-600">Durum:</span> {statusLabels[c.status] || c.status}</div>
                                      {c.reconciliation_note && (
                                        <div className="mt-1 p-1.5 bg-red-50 rounded text-red-700">
                                          <span className="font-medium">Gerekçe:</span> {c.reconciliation_note}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="p-2">{c.request_date ? new Date(c.request_date).toLocaleDateString('tr-TR') : '-'}</td>
                              <td className="p-2">{c.patient_name} {c.patient_surname}</td>
                              <td className="p-2">{c.transport_type || '-'}</td>
                              <td className="p-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${c.status === 'completed' ? 'bg-green-100 text-green-700' : c.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {statusLabels[c.status] || c.status}
                                </span>
                              </td>
                              {detailHospital?.price_per_case > 0 && (
                                <td className="p-2 text-right font-medium">
                                  {c.status === 'cancelled'
                                    ? <span className="text-red-500 line-through text-xs">0 TL</span>
                                    : <span className="text-gray-700">{(detailHospital.price_per_case).toLocaleString('tr-TR')} TL</span>
                                  }
                                </td>
                              )}
                              <td className="p-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded border ${rBadge.color}`}>
                                  {rBadge.label}
                                </span>
                                {c.reconciliation_note && (
                                  <div className="text-red-600 mt-0.5 max-w-xs truncate" title={c.reconciliation_note}>
                                    ↳ {c.reconciliation_note}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Red Gerekçesi Modal ──────────────────────────────────────── */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-2 text-red-700 flex items-center gap-2">
              <XCircleIcon className="h-5 w-5" />
              Mutabık Değiliz — Gerekçe
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{selectedCaseIds.length} vaka</strong> için "Mutabık Değiliz" gerekçesini açıklayın. Bu alan zorunludur.
            </p>
            <div className="space-y-2">
              <Label>Gerekçe Açıklaması *</Label>
              <textarea
                className="w-full border rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
                rows={4}
                placeholder="Mutabık olmama gerekçesini açıklayın..."
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>İptal</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleRejectConfirm} disabled={!rejectNote.trim() || reconciliationMutation.isLoading}>
                {reconciliationMutation.isLoading ? 'Kaydediliyor...' : 'Reddet'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── E-posta Gönder Modal ──────────────────────────────────────── */}
      {showEmailModal && detailHospital && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <EnvelopeIcon className="h-5 w-5 text-blue-600" />
              E-posta Gönder — {detailHospital.name}
            </h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>E-posta Şablonu</Label>
                <Select value={emailType} onValueChange={setEmailType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reminder">🔔 Mutabakat Hatırlatması</SelectItem>
                    <SelectItem value="reconciliation_complete">✅ Mutabakat Onay Bildirimi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Mutabakat Ayı */}
              <div className="space-y-2">
                <Label>Mutabakat Ayı</Label>
                <Select value={emailMonth} onValueChange={setEmailMonth}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {monthOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {emailPeriod && (
                  <p className="text-xs text-gray-600">
                    Dönem: <span className="font-medium text-gray-800">{emailPeriod.rangeLabel}</span>
                    {' '}({emailPeriod.monthName} {emailPeriod.year} — {emailPeriod.lastDay} gün)
                  </p>
                )}
              </div>

              {/* Alıcılar */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1 block">Alıcılar</Label>
                {[1, 2, 3].map(i => {
                  const email = detailHospital[`contact_email_${i}`];
                  const name = detailHospital[`contact_name_${i}`];
                  return email ? (
                    <div key={i} className="text-sm text-gray-700 py-0.5">
                      {name ? `${name} — ` : ''}<span className="text-blue-700">{email}</span>
                    </div>
                  ) : null;
                })}
                {!detailHospital.contact_email_1 && !detailHospital.contact_email_2 && !detailHospital.contact_email_3 && (
                  <p className="text-sm text-red-600">Bu hastane için kayıtlı e-posta adresi yok. Önce düzenleme formundan ekleyin.</p>
                )}
              </div>

              <div className="p-3 bg-gray-50 rounded text-xs text-gray-600">
                {emailType === 'reminder'
                  ? `${emailPeriod ? `${emailPeriod.monthName} ${emailPeriod.year}` : 'Seçilen'} dönemi için mutabakat hatırlatma e-postası gönderilecek.`
                  : `${emailPeriod ? `${emailPeriod.monthName} ${emailPeriod.year}` : 'Seçilen'} dönemine ait bu hastanenin vakaları Excel eki olarak mutabakat onay bildirimiyle birlikte gönderilecek.`}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowEmailModal(false)}>İptal</Button>
              <Button onClick={handleSendEmail} disabled={isSendingEmail || (!detailHospital.contact_email_1 && !detailHospital.contact_email_2 && !detailHospital.contact_email_3)}>
                {isSendingEmail ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Hospital Create / Edit Modal ────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">{editingHospital ? 'Hastane Düzenle' : 'Yeni Hastane'}</h2>
              <Button variant="ghost" size="sm" onClick={handleCloseModal}><XMarkIcon className="h-5 w-5" /></Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Hastane Adı *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Hastane adını girin" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Hastane Kodu</Label>
                  <Input id="code" value={formData.code} onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))} placeholder="Hastane kodunu girin" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Hastane Türü *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}>
                    <SelectTrigger><SelectValue placeholder="Hastane türünü seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DEVLET_HASTANESI">Devlet Hastanesi</SelectItem>
                      <SelectItem value="EGITIM_ARASTIRMA">Eğitim Araştırma Hastanesi</SelectItem>
                      <SelectItem value="OZEL_HASTANE">Özel Hastane</SelectItem>
                      <SelectItem value="UNIVERSITESI">Üniversite Hastanesi</SelectItem>
                      <SelectItem value="SAGLIK_MERKEZI">Sağlık Merkezi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Başkanlık</Label>
                  <Select value={formData.department_id} onValueChange={(value) => setFormData(prev => ({ ...prev, department_id: value }))}>
                    <SelectTrigger><SelectValue placeholder={institutionsLoading ? 'Yükleniyor...' : 'Başkanlık seçin'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Başkanlık seçilmedi</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id.toString()}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">İl</Label>
                  <Input id="city" value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} placeholder="İl adını girin" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district">İlçe</Label>
                  <Input id="district" value={formData.district} onChange={(e) => setFormData(prev => ({ ...prev, district: e.target.value }))} placeholder="İlçe adını girin" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="monthly_quota">Genel Kota</Label>
                  <Input id="monthly_quota" type="number" value={formData.monthly_quota} onChange={(e) => setFormData(prev => ({ ...prev, monthly_quota: e.target.value }))} placeholder="0" min="0" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_per_case">Vaka Başı Ücret (TL)</Label>
                  <Input id="price_per_case" type="number" value={formData.price_per_case} onChange={(e) => setFormData(prev => ({ ...prev, price_per_case: e.target.value }))} placeholder="0" min="0" step="0.01" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input id="phone" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} placeholder="Telefon numarasını girin" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Adres</Label>
                  <Input id="address" value={formData.address} onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))} placeholder="Adres bilgisini girin" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="quota_note">Kota Notu</Label>
                  <Input id="quota_note" value={formData.quota_note} onChange={(e) => setFormData(prev => ({ ...prev, quota_note: e.target.value }))} placeholder="Kota ile ilgili açıklama" />
                </div>

                {/* E-posta & Yetkili Kişiler */}
                <div className="md:col-span-2">
                  <div className="border-t pt-4 mt-2">
                    <Label className="text-sm font-semibold text-gray-800 flex items-center gap-1 mb-3">
                      <EnvelopeIcon className="h-4 w-4" />
                      Yetkili Kişiler ve E-posta Adresleri
                    </Label>
                    <div className="space-y-3">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-600">Yetkili {i} Adı Soyadı</Label>
                            <Input
                              value={formData[`contact_name_${i}`]}
                              onChange={(e) => setFormData(prev => ({ ...prev, [`contact_name_${i}`]: e.target.value }))}
                              placeholder={`Yetkili ${i} adı soyadı`}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-600">E-posta Adresi {i}</Label>
                            <Input
                              type="email"
                              value={formData[`contact_email_${i}`]}
                              onChange={(e) => setFormData(prev => ({ ...prev, [`contact_email_${i}`]: e.target.value }))}
                              placeholder={`yetkili${i}@hastane.com`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={handleCloseModal} disabled={createHospitalMutation.isLoading || updateHospitalMutation.isLoading}>İptal</Button>
                <Button type="submit" disabled={createHospitalMutation.isLoading || updateHospitalMutation.isLoading}>
                  {createHospitalMutation.isLoading || updateHospitalMutation.isLoading ? 'Kaydediliyor...' : editingHospital ? 'Güncelle' : 'Kaydet'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalsPage;
